import { createPortal } from "react-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";
import { nativeSheetScrollBottomPad } from "@/lib/native/nativeSheetPadding";

type NativeOverlaySheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  /** Prevent Radix from auto-focusing the first field (e.g. search input). */
  disableAutoFocus?: boolean;
};

/** Full-width bottom sheet on native; centered modal fallback on web. */
export const NativeOverlaySheet = ({
  open,
  onOpenChange,
  title,
  children,
  className,
  contentClassName,
  disableAutoFocus = false,
}: NativeOverlaySheetProps) => {
  const { isNative } = useNativePlatform();

  if (!isNative) {
    if (!open) return null;
    return createPortal(
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
        onClick={() => onOpenChange(false)}
      >
        <div
          className={cn(
            "relative z-10 w-full max-w-lg rounded-xl border bg-card p-5 shadow-lg max-h-[85dvh] overflow-y-auto",
            className,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {title && <h2 className="text-lg font-semibold mb-3 pr-8">{title}</h2>}
          {children}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        expandable={false}
        // Always nest above parent sheets (e.g. För dig full-ad z-211)
        nestedSheet
        overlayClassName="!z-[230]"
        className={cn(
          "native-sheet-compact w-full max-w-none !z-[231]",
          className,
        )}
        onOpenAutoFocus={
          disableAutoFocus ? (event) => event.preventDefault() : undefined
        }
      >
        {title && (
          <SheetHeader className="text-left shrink-0 pb-2">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
        )}
        <div
          className={cn(
            "mt-6 overflow-y-auto overscroll-contain touch-pan-y flex-1 min-h-0 -mx-1 px-1",
            nativeSheetScrollBottomPad,
            contentClassName,
          )}
        >
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
};
